import type {
  PublicDocumentResponse,
  PublicPermitResponse,
} from '@contracts/order/PublicOrderResponse';
import { MobileAccordion } from '@src/common/components/mobile/MobileAccordion';
import { MobileDetailItems } from '@src/common/components/mobile/MobileDetailItems';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { DownloadService } from '@src/common/services/DownloadService';
import { Timestamp } from '@src/common/time/Timestamp';
import { PublicDocumentClient } from '@src/documents/services/PublicDocumentClient';
import { PermitStatusBadge } from '@src/orders/components/PermitStatusBadge';
import { transformToPermitStatus } from '@src/orders/domain/PermitApplication';
import { PublicConditionsSheetsClient } from '@src/orders/services/PublicConditionsSheetsClients';
import { PublicDocumentList } from '@src/publicPortal/PublicDocumentList';
import { useTranslation } from 'react-i18next';

const getDocumentName = (doc: PublicDocumentResponse) =>
  doc.displayName ?? doc.key.substring(doc.key.lastIndexOf('/') + 1);

interface Props {
  permit: PublicPermitResponse;
}

export const PermitCard = ({ permit }: Props) => {
  const { t } = useTranslation();

  const items = [
    {
      label: t('publicPortal.permit.number'),
      value: permit.permitNumber ?? t('publicPortal.permit.comingSoon'),
    },
    {
      label: t('publicPortal.permit.effectiveDate'),
      value: Timestamp.from(permit.effectiveDate).formatUTCDate(),
    },
  ];

  return (
    <MobileAccordion
      id={permit.id}
      summary={<PermitCardSummary permit={permit} />}
    >
      <Stack spacing={0}>
        <MobileDetailItems items={items} spacing={0.5} sx={{ mt: -2.25 }} />
        <PermitDocuments
          documents={permit.documents}
          conditionsSheets={permit.conditionsSheets}
        />
      </Stack>
    </MobileAccordion>
  );
};

const PermitDocuments = ({
  documents,
  conditionsSheets,
}: {
  documents: PublicDocumentResponse[];
  conditionsSheets: PublicDocumentResponse[];
}) => {
  const { t } = useTranslation();
  const hasDocuments = documents.length || conditionsSheets.length;
  if (!hasDocuments) {
    return null;
  }

  const downloadDocument = async (doc: PublicDocumentResponse) => {
    const filename = getDocumentName(doc);
    const { blob } = await PublicDocumentClient.download(doc.token, filename);
    await DownloadService.download(blob, filename);
  };

  const downloadConditionSheet = async (
    conditionsSheet: PublicDocumentResponse,
  ) => {
    const filename = getDocumentName(conditionsSheet);
    const { blob } = await PublicConditionsSheetsClient.download(
      conditionsSheet.token,
    );
    await DownloadService.download(blob, filename);
  };

  return (
    <Stack>
      <Typography variant="body2" style={{ marginTop: '10px' }}>
        {t('publicPortal.documentsSection')}
      </Typography>
      <PublicDocumentList documents={documents} onDownload={downloadDocument} />
      <PublicDocumentList
        documents={conditionsSheets}
        onDownload={downloadConditionSheet}
      />
    </Stack>
  );
};

const PermitCardSummary = ({ permit }: { permit: PublicPermitResponse }) => {
  return (
    <Stack direction="column" spacing={0.5}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {permit.jurisdiction}
        </Typography>
        <PermitStatusBadge status={transformToPermitStatus(permit.status)} />
      </Stack>
      <Typography variant="body2">{permit.permitType}</Typography>
    </Stack>
  );
};
